import { useState, useCallback } from "react";
import * as React from "react";
import { useContext } from "react";
import { toast } from "react-toastify";
import ReportAPI from "src/admin/report/ReportAPI";
import LocationAPI from "src/admin/Organization/Location/LocationAPI";
import CoreAPI from "src/core/CoreAPI";
import PickListContext from "src/context/pickListContext";
import { Colours } from "src/branding/ImpiricaBrand";
import Utils from "src/common/utils";
import { AdminPermissions } from "src/common/Permissions";
import { notifyV1ReportsNotSupported } from "src/components/common/V1ReportsNotSupported";
import { useApi } from "src/Api/useApi";
import {
	Assessment,
	DatasetPurposeType,
	LastModified,
} from "src/@types/ImpiricaPlatformApi";

interface FetchReportParams {
	reportId: number;
	existingReportId: number | undefined;
	dataLocked: boolean;
	datasetProductId: ProductType | undefined;
	datasetId: number;
	organizationId: number;
	currentUser?: any;
	commercialDrivingExpSettings?: CommercialDrivingExpSettingsDTO;
	showEmployeeId?: boolean;
}

export function useReportGeneration() {
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	const pickListItems = useContext(PickListContext);
	const {
		tripInspectionClient,
		assessmentClient,
		impiricaUserClient,
		datasetsClient,
		auditLogClient,
	} = useApi();

	const fetchV1ReportData = useCallback(async () => {
		// V1 (HTML-to-PDF) report generation was retired.
		notifyV1ReportsNotSupported();
	}, []);

	const fetchV2ReportData = useCallback(
		async (
			params: FetchReportParams,
			clientSiteReportInfo: ReportNecessaryInfoDTO
		) => {
			try {
			const {
				organizationId,
				datasetId,
				reportId,
				existingReportId,
				dataLocked,
				datasetProductId,
				currentUser,
				commercialDrivingExpSettings,
				showEmployeeId,
			} = params;
			let reportFetched = false;

			if (dataLocked && existingReportId && existingReportId !== 0) {
				ReportAPI.clientSiteGenerateV2ReportPDF({
					reportId,
					datasetId,
				});
				reportFetched = true;
			} else {
				let clientSiteGetCoreTestForReport = {};
				let clientSiteGetCareTestForReport = {};
				let clientSiteClientCoreErrorEntries = [{}];

				const clientSiteReportInfoSettings =
					await ReportAPI.getClientSiteReportSettings({
						datasetId,
						reportId,
					});
				if (!clientSiteReportInfoSettings) {
					toast.error("Failed to get participant report info settings");
					return;
				}

				const clientSiteOrganizationLocations =
					await LocationAPI.getClientSiteOrganizationLocations(
						organizationId,
						datasetId
					);
				if (!clientSiteOrganizationLocations) {
					toast.error("Failed to get locations");
					return;
				}

				if (
					clientSiteReportInfo.coreTestResultId &&
					clientSiteReportInfo.coreScore !== undefined
				) {
					clientSiteGetCoreTestForReport =
						await ReportAPI.clientSiteGetCoreTestForReport({
							datasetId,
							reportId,
							coreTestResultId: clientSiteReportInfo.coreTestResultId,
						});
					if (!clientSiteGetCoreTestForReport) {
						toast.error("Failed to get core test for report");
						return;
					}

					clientSiteClientCoreErrorEntries =
						await CoreAPI.getClientSiteClientCoreErrorReportEntries({
							datasetId,
							coreTestResultId: clientSiteReportInfo.coreTestResultId,
						});
					if (!clientSiteClientCoreErrorEntries) {
						toast.error("Failed to get core test entries");
						return;
					}
				}

				if (
					clientSiteReportInfo.careTestResultId &&
					(clientSiteReportInfo.careAborted ||
						clientSiteReportInfo.careScore !== undefined)
				) {
					clientSiteGetCareTestForReport =
						await ReportAPI.clientSiteGetCareTestForReport({
							datasetId,
							reportId,
							careTestResultId: clientSiteReportInfo.careTestResultId,
						});
					if (!clientSiteGetCareTestForReport) {
						toast.error("Failed to get care test for report");
						return;
					}
				}

				let coreMapImagePath = "";
				if (
					clientSiteReportInfo.coreTestResultId &&
					clientSiteReportInfo.coreTestResultId > 0
				) {
					// Load map image for CORE
					coreMapImagePath = await CoreAPI.getGoogleMap({
						coreTestResultId: clientSiteReportInfo.coreTestResultId,
						routeColor: Colours.tertiary,
						errorColor: Colours.magenta,
						hazardousErrorColor: Colours.primary,
					});
				}

				let assessmentResult: Assessment | undefined = undefined;
				if (
					currentUser &&
					currentUser.permissions!.includes(AdminPermissions.GlobalAdmin)
				) {
					assessmentResult = await assessmentClient!.getAssessmentResultForAdmin(
						reportId
					);
				} else {
					assessmentResult = await assessmentClient!.getAssessmentResult(
						reportId,
						organizationId.toString(),
						datasetId.toString()
					);
				}

				let createdDate = assessmentResult!.dateCreated;
				const neurapulseRecommendation = Utils.mapNeurapulseRecommendationType(
					assessmentResult!.neurapulseRecommendation
				);
				const neurapulseDateCompleted = assessmentResult!.neurapulseCompleted;

				let lastModified: LastModified | undefined = undefined;
				if (auditLogClient) {
					try {
						let auditLogResults =
							await auditLogClient.getAssessmentModifiedAuditLog(
								datasetId,
								reportId
							);
						lastModified = auditLogResults;
					} catch (error) {
						console.error("Failed to get audit logs for assessment report.");
					}
				}

				let tripInspection = assessmentResult!.tripInspection;
				let taskTypes = tripInspection
					? await tripInspectionClient!.getTripTasks(
							tripInspection.vehicleSubTypeId
					  )
					: undefined;
				let observableTypes =
					await tripInspectionClient!.getAllTripObservationTypes();

				let tripInspectionUser = tripInspection
					? await impiricaUserClient!.getImpiricaUser(
							tripInspection.createdBy,
							organizationId
					  )
					: undefined;

				const datasetPurposeType: DatasetPurposeType =
					await datasetsClient!.getDatasetPurpose(datasetId);

				// react-pdf + templates load on demand (separate chunk)
				const { pdf, ReportTemplates } = await import("src/admin/report/pdfRuntime");
				const reportTemplate = await pdf(
					React.createElement(ReportTemplates, {
						report: clientSiteReportInfo,
						reportSetting: clientSiteReportInfoSettings,
						locations: clientSiteOrganizationLocations,
						neurapulseRecommendation: neurapulseRecommendation,
						neurapulseDateCompleted: neurapulseDateCompleted,
						coreTestResult: clientSiteGetCoreTestForReport,
						careTestResult: clientSiteGetCareTestForReport,
						coreErrorEntries: clientSiteClientCoreErrorEntries,
						productType: datasetProductId,
						mapImagePath: coreMapImagePath,
						picklistContext: pickListItems,
						tripInspection: tripInspection,
						tripTasks: taskTypes,
						observableTypes: observableTypes,
						tripInspectionUser: tripInspectionUser,
						watermark: datasetPurposeType.watermark,
						datasetPurposeTypeName: datasetPurposeType.name,
						commercialDrivingExpSettings: commercialDrivingExpSettings,
						showEmployeeId: showEmployeeId,
						sessionDateCreated: createdDate,
						lastModified: lastModified,
					}) as any
				).toBlob();

				const reportAdminTemplate = await pdf(
					React.createElement(ReportTemplates, {
						report: clientSiteReportInfo,
						reportSetting: clientSiteReportInfoSettings,
						locations: clientSiteOrganizationLocations,
						neurapulseRecommendation: neurapulseRecommendation,
						neurapulseDateCompleted: neurapulseDateCompleted,
						coreTestResult: clientSiteGetCoreTestForReport,
						careTestResult: clientSiteGetCareTestForReport,
						coreErrorEntries: clientSiteClientCoreErrorEntries,
						redactInfo: true,
						productType: datasetProductId,
						mapImagePath: coreMapImagePath,
						picklistContext: pickListItems,
						tripInspection: tripInspection,
						tripTasks: taskTypes,
						observableTypes: observableTypes,
						tripInspectionUser: tripInspectionUser,
						watermark: datasetPurposeType.watermark,
						datasetPurposeTypeName: datasetPurposeType.name,
						commercialDrivingExpSettings: commercialDrivingExpSettings,
						showEmployeeId: showEmployeeId,
						sessionDateCreated: createdDate,
						lastModified: lastModified,
					}) as any
				).toBlob();

				const report = new File([reportTemplate], "report");
				const adminReport = new File([reportAdminTemplate], "adminReport");
				await ReportAPI.clientSiteSaveReportV2Data({ reportId, datasetId }, [
					report,
					adminReport,
				]);
				ReportAPI.clientSiteGenerateV2ReportPDF({
					reportId,
					datasetId,
				});
				reportFetched = true;
			}
			return reportFetched;
			} catch (error) {
				toast.error("Something went wrong");
				return false;
			}
		},
		[
			pickListItems,
			tripInspectionClient,
			assessmentClient,
			impiricaUserClient,
			datasetsClient,
			auditLogClient,
		]
	);

	const fetchReportData = useCallback(
		async (params: FetchReportParams) => {
			setIsGeneratingPdf(true);
			try {
				const clientSiteReportInfo = await ReportAPI.getClientSiteReport({
					datasetId: params.datasetId,
					reportId: params.reportId,
				});

				if (!clientSiteReportInfo) {
					toast.error("Failed to get participant report info");
					return false;
				}

				if (
					params.datasetProductId === "ExceleRATEV2" ||
					params.datasetProductId === "DriveABLE"
				) {
					await fetchV2ReportData(params, clientSiteReportInfo);
				} else {
					await fetchV1ReportData();
				}

				return true;
			} finally {
				setIsGeneratingPdf(false);
			}
		},
		[fetchV1ReportData, fetchV2ReportData]
	);

	return { fetchReportData, isGeneratingPdf };
}
